import React, { useState, useEffect } from 'react'

const ImageGalleryModal = ({ images, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [zoom, setZoom] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })

  // Check if current media is video
  const isVideo = (src) => /\.(mp4|webm|ogg|mov)$/i.test(src)
  const currentIsVideo = isVideo(images[currentIndex])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') handlePrev()
      if (e.key === 'ArrowRight') handleNext()
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [currentIndex])

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3))
  }

  const handleZoomOut = () => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - 0.5, 1)
      if (newZoom === 1) setPosition({ x: 0, y: 0 })
      return newZoom
    })
  }

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    }
  }

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  return (
    <div 
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors z-50"
        aria-label="Close"
      >
        <span className="text-3xl font-light">×</span>
      </button>

      {/* Title */}
      <div className="absolute top-6 left-6 text-white z-50">
        <h2 className="text-xl font-light playfair">Gallery</h2>
      </div>

      {/* Media Container */}
      <div 
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: zoom > 1 && !currentIsVideo ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        {currentIsVideo ? (
          <video
            src={images[currentIndex]}
            className="max-w-[90vw] max-h-[90vh] object-contain select-none"
            autoPlay
            loop
            muted
            playsInline
            controls
          />
        ) : (
          <img
            src={images[currentIndex]}
            alt={`Gallery ${currentIndex + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain select-none"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              transition: isDragging ? 'none' : 'transform 0.3s ease'
            }}
            draggable={false}
          />
        )}
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handlePrev()
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors z-50"
            aria-label="Previous"
          >
            <svg className="w-8 h-8" viewBox="0 0 60 60" fill="currentColor">
              <path d="M29 43l-3 3-16-16 16-16 3 3-13 13 13 13z" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleNext()
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors z-50"
            aria-label="Next"
          >
            <svg className="w-8 h-8 rotate-180" viewBox="0 0 60 60" fill="currentColor">
              <path d="M29 43l-3 3-16-16 16-16 3 3-13 13 13 13z" />
            </svg>
          </button>
        </>
      )}

      {/* Zoom Controls - hide for videos */}
      {!currentIsVideo && (
        <div className="absolute bottom-6 right-6 flex gap-2 z-50">
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleZoomOut()
            }}
            disabled={zoom <= 1}
            className="w-10 h-10 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Zoom Out"
          >
            <span className="text-2xl font-light">−</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleZoomIn()
            }}
            disabled={zoom >= 3}
            className="w-10 h-10 flex items-center justify-center text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Zoom In"
          >
            <span className="text-2xl font-light">+</span>
          </button>
        </div>
      )}

      {/* Bullets Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-50">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation()
                setCurrentIndex(idx)
                setZoom(1)
                setPosition({ x: 0, y: 0 })
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? 'bg-white w-8' : 'bg-white/50'
              }`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* Zoom Icon Indicator - hide for videos */}
      {zoom === 1 && !currentIsVideo && (
        <div className="absolute bottom-20 right-6 text-white/50 z-50">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
          </svg>
        </div>
      )}
    </div>
  )
}

export default ImageGalleryModal
